import {
  collectError,
  renderPersonalizationSource,
  renderPersonalizationText,
  type PersonalizationCatalogSample,
  type PersonalizationField,
  type PersonalizationRenderError,
  type PersonalizationTemplateLanguage,
} from "@hightouch/lib/llm/lifecycle-studio/personalization-preview/renderer";
import type { PersonalizationPrimitive } from "@hightouch/lib/llm/lifecycle-studio/personalization-preview/preview-variables";

export type {
  PersonalizationCatalogSample,
  PersonalizationField,
  PersonalizationRenderError,
} from "@hightouch/lib/llm/lifecycle-studio/personalization-preview/renderer";
import type {
  ContentEmailPersonalizationPreviewQuery,
  ContentEmailPersonalizationVariable,
} from "src/graphql";

export type PersonalizationPreview =
  ContentEmailPersonalizationPreviewQuery["getContentEmailPersonalizationPreview"];

// Human-readable names for each part of a message, used in the parse-failure
// toast. Keyed by `PersonalizationField` so a new field is a compile error here.
const FIELD_LABELS: Record<PersonalizationField, string> = {
  subject: "subject line",
  preheader: "preheader",
  html: "email body",
  body: "message body",
  title: "title",
};

/** Shared by every personalization preview surface (sidebar panel, canvas
 * Preview tab) so their parse-failure toasts dedupe to one. */
export const PARSE_ERROR_TOAST_ID = "personalization-preview-parse-error";

export function formatRenderErrorMessage(
  errors: PersonalizationRenderError[],
): string | null {
  const [first, ...rest] = errors;
  if (!first) return null;
  const detail = `The ${FIELD_LABELS[first.field]} could not be rendered: ${first.message}`;
  return rest.length > 0 ? `${detail} (+${rest.length} more)` : detail;
}

export type PersonalizationVariable = ContentEmailPersonalizationVariable;

export type PersonalizationPreviewOption = {
  label: string;
  values: Record<string, PersonalizationPrimitive>;
};

export type PersonalizationPreviewValueHistory = Record<string, string[]>;

export type PreviewMessageSource =
  | {
      channel: "email";
      subject?: string | null;
      preheader?: string | null;
      html: string;
    }
  | {
      channel: "sms";
      body: string;
    }
  | {
      channel: "push";
      title: string;
      body: string;
    };

export type RenderedPreviewMessage =
  | {
      channel: "email";
      subject: string | null;
      preheader: string | null;
      html: string;
    }
  | {
      channel: "sms";
      body: string;
    }
  | {
      channel: "push";
      title: string;
      body: string;
    };

// Liquid and Handlebars tags both open with `{{` or `{%`. Content-block tags
// (`{% ht_content_block … %}`) match too, which errs on the safe side: a
// referenced block's html may contain variables the raw editor html doesn't.
const TEMPLATE_SYNTAX_RE = /\{\{|\{%/;

export function containsTemplateSyntax(
  ...parts: Array<string | null | undefined>
): boolean {
  return parts.some(
    (part) => typeof part === "string" && TEMPLATE_SYNTAX_RE.test(part),
  );
}

export function buildPersonalizationPreviewOptions(
  preview: PersonalizationPreview | undefined,
): PersonalizationPreviewOption[] {
  return (preview?.options ?? []).map((option, index) => ({
    label: `Option ${index + 1}`,
    values: normalizeValues(option.values),
  }));
}

export function normalizeValues(
  raw: unknown,
): Record<string, PersonalizationPrimitive> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const values: Record<string, PersonalizationPrimitive> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      values[key] = value;
    }
  }
  return values;
}

export function valuesForOption(
  option: PersonalizationPreviewOption | undefined,
): Record<string, string> {
  if (!option) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(option.values).map(([key, value]) => [key, String(value)]),
  );
}

export function appendPersonalizationPreviewValueHistory(
  history: PersonalizationPreviewValueHistory | undefined,
  values: Record<string, string>,
): PersonalizationPreviewValueHistory {
  const nextHistory: PersonalizationPreviewValueHistory = {
    ...(history ?? {}),
  };

  for (const [key, value] of Object.entries(values)) {
    nextHistory[key] = [...(nextHistory[key] ?? []), value];
  }

  return nextHistory;
}

export function renderPersonalizationPreview(
  source: PreviewMessageSource,
  values: Record<string, PersonalizationPrimitive>,
  language: PersonalizationTemplateLanguage,
  catalogs: PersonalizationCatalogSample[] = [],
  errors?: PersonalizationRenderError[],
): RenderedPreviewMessage {
  switch (source.channel) {
    case "email":
      return {
        channel: "email",
        ...renderPersonalizationSource(
          source,
          values,
          language,
          catalogs,
          errors,
        ),
      };
    case "sms":
      return {
        channel: "sms",
        body: renderPersonalizationText(
          source.body,
          values,
          language,
          catalogs,
          collectError(errors, "body"),
        ),
      };
    case "push":
      return {
        channel: "push",
        title: renderPersonalizationText(
          source.title,
          values,
          language,
          catalogs,
          collectError(errors, "title"),
        ),
        body: renderPersonalizationText(
          source.body,
          values,
          language,
          catalogs,
          collectError(errors, "body"),
        ),
      };
  }
}
